import {CreateSitePage} from '@/pages/sites/CreateSitePage';
import {Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {Link, useSearchParams} from 'react-router-dom';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {Spinner} from '@/components/ui/Spinner';
import {downloadInvoice} from '@/api/billing';
import {useAccountOverrides, usePricing, useSiteInvoices, useUsage} from '@/hooks/useBilling';
import {useEffect, useMemo, useState} from 'react';
import type {Invoice, PlanId, SiteUsageData} from '@/types/api';

function UsageCard({label, used, limit, percent, isOver, unit = ''}: {
    label: string;
    used: number;
    limit: number;
    percent: number;
    isOver: boolean;
    unit?: string;
}) {
    const isWarning = !isOver && percent > 80;
    const limitDisplay = limit === -1 ? 'Unlimited' : `${formatNumber(limit)}${unit}`;

    return (
        <article className={`studio-billing-usage-card${isOver ? ' is-over' : isWarning ? ' is-warning' : ''}`}>
            <span className="studio-billing-usage-heading">{label}</span>
            <div className="studio-billing-usage-value">
                <strong>{formatNumber(used)}{unit}</strong>
                <span>of {limitDisplay}</span>
            </div>
            <div aria-label={`${label}: ${formatNumber(used)} of ${limitDisplay}`} aria-valuemax={limit === -1 ? undefined : limit} aria-valuemin={0} aria-valuenow={used} className="studio-billing-progress" role="progressbar">
                <i style={{width: `${Math.min(percent, 100)}%`}} />
            </div>
            <p>{isOver ? 'Limit exceeded' : isWarning ? 'Approaching the plan limit' : limit === -1 ? 'Included without a limit' : `${formatNumber(Math.max(limit - used, 0))}${unit} remaining`}</p>
        </article>
    );
}

function FeatureItem({name, enabled}: {name: string; enabled: boolean}) {
    const formattedName = name.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    return (
        <div className={`studio-billing-feature${enabled ? ' is-enabled' : ' is-disabled'}`}>
            <span aria-hidden="true">{enabled ? <LucideIcon.Check size={14} /> : <LucideIcon.X size={13} />}</span>
            <span>{formattedName}</span>
        </div>
    );
}

function formatDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
}

function formatCurrency(cents: number, currency: string = 'USD') {
    const code = currency.toUpperCase();
    const symbols: Record<string, string> = {USD: '$', EUR: '€', GBP: '£'};
    const [whole, fraction] = (cents / 100).toFixed(2).split('.');
    const amount = `${formatNumber(Number(whole))}.${fraction}`;
    return symbols[code] ? `${symbols[code]}${amount}` : `${amount} ${code}`;
}

function InvoiceStatusBadge({status}: {status: string}) {
    return <span className={`studio-billing-invoice-status is-${status}`}>{status}</span>;
}

function getSiteStatus(site: SiteUsageData): 'ok' | 'warning' | 'over' {
    const metrics = [site.usage.staff, site.usage.members, site.usage.newsletters, site.usage.storage_mb, site.usage.email_sends_monthly];
    if (metrics.some(metric => metric.isOver)) {
        return 'over';
    }
    if (metrics.some(metric => metric.percent > 80)) {
        return 'warning';
    }
    return 'ok';
}

function CreateNewsletterDialog({open: isOpen, onOpenChange}: {open: boolean; onOpenChange: (open: boolean) => void}) {
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        document.body.classList.add('studio-create-dialog-open');
        return () => document.body.classList.remove('studio-create-dialog-open');
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="studio-active studio-create-dialog">
                <DialogClose asChild>
                    <button aria-label="Close create newsletter" className="studio-create-dialog-close" type="button"><LucideIcon.X size={18} /></button>
                </DialogClose>
                <DialogHeader className="studio-create-dialog-heading">
                    <DialogTitle>Create a newsletter</DialogTitle>
                    <DialogDescription>Choose a name and address for your publication.</DialogDescription>
                </DialogHeader>
                <CreateSitePage modal />
            </DialogContent>
        </Dialog>
    );
}

export function BillingPage() {
    const accountOverrides = useAccountOverrides();
    const [searchParams, setSearchParams] = useSearchParams();
    const {data: usageData, isLoading: usageLoading} = useUsage();
    const {data: pricingData} = usePricing();
    const sites = useMemo(() => usageData || [], [usageData]);
    const selectedSiteId = searchParams.get('site');
    const [activeSiteId, setActiveSiteId] = useState<string | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);

    useEffect(() => {
        if (!sites.length) {
            setActiveSiteId(null);
            return;
        }

        const siteExists = sites.some(site => site.tenant_id === selectedSiteId);
        setActiveSiteId(selectedSiteId && siteExists ? selectedSiteId : sites[0].tenant_id);
    }, [selectedSiteId, sites]);

    const {data: invoicesData, isLoading: invoicesLoading} = useSiteInvoices(activeSiteId || undefined, 5);
    const invoices = invoicesData?.invoices || [];
    const activeSite = sites.find(site => site.tenant_id === activeSiteId);

    const handleSiteSelect = (siteId: string) => {
        setActiveSiteId(siteId);
        setSearchParams({site: siteId});
    };

    const getPlanName = (planId: PlanId | null) => {
        if (!planId || !pricingData?.plans) {
            return 'Free';
        }
        return pricingData.plans.find(plan => plan.id === planId)?.name || 'Free';
    };

    const handleDownloadInvoice = async (invoiceId: string, invoiceNumber: string) => {
        try {
            const blob = await downloadInvoice(invoiceId);
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `invoice-${invoiceNumber}.pdf`;
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(anchor);
        } catch {
        }
    };

    if (usageLoading) {
        return (
            <div className="studio-content-page studio-billing-loading" role="status">
                <Spinner size={80} />
                <p>Loading billing data…</p>
            </div>
        );
    }

    if (!sites.length) {
        return (
            <div className="studio-content-page studio-billing-page">
                {accountOverrides.data?.map(row => <p key={row.key}>Support override — Publications: {row.value === -1 ? 'Unlimited' : row.value}{row.expires_at ? ` (until ${formatDate(row.expires_at)})` : ''}. Contact support for changes.</p>)}
                <header className="studio-page-heading studio-billing-heading">
                    <div><h1>Billing</h1><p className="studio-billing-intro">Manage plans, usage, and invoices for each newsletter.</p></div>
                </header>
                <div className="studio-billing-empty">
                    <h2>No newsletters yet</h2>
                    <p>Create your first newsletter to start tracking usage and managing a subscription.</p>
                    <button className="studio-control studio-control-primary" type="button" onClick={() => setShowCreateDialog(true)}>Create newsletter</button>
                </div>
                <CreateNewsletterDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
            </div>
        );
    }

    const planName = activeSite ? getPlanName(activeSite.plan_id) : '';
    const activePlan = pricingData?.plans.find(plan => plan.id === activeSite?.plan_id);
    const hiddenFeatures = ['sso_saml', 'priority_support'];
    const featureEntries = activeSite ? Object.entries(activeSite.features).filter(([key]) => !hiddenFeatures.includes(key)) : [];
    const enabledFeatures = featureEntries.filter(([, enabled]) => enabled);
    const disabledFeatures = featureEntries.filter(([, enabled]) => !enabled);

    return (
        <div className="studio-content-page studio-billing-page">
            {accountOverrides.data?.map(row => <p key={row.key}>Support override — Publications: {row.value === -1 ? 'Unlimited' : row.value}{row.expires_at ? ` (until ${formatDate(row.expires_at)})` : ''}. Contact support for changes.</p>)}
            <header className="studio-page-heading studio-billing-heading">
                <div>
                    <h1>Billing</h1>
                    <p className="studio-billing-intro">Manage plans, usage, and invoices for each newsletter.</p>
                </div>
                <Link className="studio-control studio-control-primary studio-billing-change-plan" to="/billing/upgrade">
                    Change plan <LucideIcon.ArrowUpRight size={16} />
                </Link>
            </header>

            <nav aria-label="Choose a newsletter" className="studio-billing-site-tabs">
                {sites.map((site) => {
                    const status = getSiteStatus(site);
                    return (
                        <button key={site.tenant_id} aria-current={site.tenant_id === activeSiteId ? 'page' : undefined} type="button" onClick={() => handleSiteSelect(site.tenant_id)}>
                            {site.site_name}
                            {status !== 'ok' && <LucideIcon.TriangleAlert className={`is-${status}`} size={13} />}
                        </button>
                    );
                })}
                <button className="studio-billing-add-site" type="button" onClick={() => setShowCreateDialog(true)}><LucideIcon.Plus size={14} />Add newsletter</button>
            </nav>

            {activeSite && <>
                <section className="studio-billing-plan-hero">
                    <div className="studio-billing-plan-main">
                        <div>
                            <p>Current plan for {activeSite.site_name}</p>
                            <h2>{planName}</h2>
                            <span>{activePlan?.description || 'Everything this publication needs to grow.'}</span>
                        </div>
                    </div>
                    <div className="studio-billing-plan-summary">
                        <strong>{formatNumber(enabledFeatures.length)}</strong>
                        <span>included features</span>
                    </div>
                </section>

                {(activeSite.complimentary || !!activeSite.overrides?.length) && <section aria-label="Account overrides" className="studio-panel" style={{padding: 20}}>
                    {activeSite.complimentary && <p>Complimentary — {activeSite.complimentary.plan_name}{activeSite.complimentary.expires_at ? ` (until ${formatDate(activeSite.complimentary.expires_at)})` : ''}</p>}
                    <p>Support has adjusted this publication’s access. Existing paid subscriptions continue billing. Contact support to change these overrides.</p>
                    {activeSite.overrides?.filter(row => row.kind !== 'plan').map(row => <p key={`${row.kind}:${row.key}`}>{row.key.replace(/_/g, ' ')}: {row.value === -1 ? 'Unlimited' : String(row.value)}{row.expires_at ? ` (until ${formatDate(row.expires_at)})` : ''}</p>)}
                </section>}
                <section aria-labelledby="usage-heading" className="studio-billing-section">
                    <div className="studio-billing-section-heading">
                        <h2 id="usage-heading">Usage</h2>
                        <p>Managed email usage includes TribeTale sends only. Bring-your-own sends are billed by your provider and do not count toward managed email usage or caps.</p>
                        <p>Live limits for <strong>{activeSite.site_name}</strong></p>
                    </div>
                    <div className="studio-billing-usage-grid">
                        <UsageCard isOver={activeSite.usage.staff.isOver} label="Staff users" limit={activeSite.usage.staff.limit} percent={activeSite.usage.staff.percent} used={activeSite.usage.staff.used} />
                        <UsageCard isOver={activeSite.usage.members.isOver} label="Members" limit={activeSite.usage.members.limit} percent={activeSite.usage.members.percent} used={activeSite.usage.members.used} />
                        <UsageCard isOver={activeSite.usage.newsletters.isOver} label="Newsletters" limit={activeSite.usage.newsletters.limit} percent={activeSite.usage.newsletters.percent} used={activeSite.usage.newsletters.used} />
                        <UsageCard isOver={activeSite.usage.storage_mb.isOver} label="Storage" limit={activeSite.usage.storage_mb.limit} percent={activeSite.usage.storage_mb.percent} unit=" MB" used={activeSite.usage.storage_mb.used} />
                        <UsageCard isOver={activeSite.usage.email_sends_monthly.isOver} label="Emails this month" limit={activeSite.usage.email_sends_monthly.limit} percent={activeSite.usage.email_sends_monthly.percent} used={activeSite.usage.email_sends_monthly.used} />
                    </div>
                </section>

                <section aria-labelledby="features-heading" className="studio-billing-section studio-billing-feature-panel">
                    <div className="studio-billing-feature-copy">
                        <h2 id="features-heading">Included features</h2>
                        <p>Available on the {planName} plan.</p>
                    </div>
                    <div className="studio-billing-feature-grid">
                        {[...enabledFeatures, ...disabledFeatures].map(([key, enabled]) => <FeatureItem key={key} enabled={enabled} name={key} />)}
                    </div>
                </section>

                <section aria-labelledby="invoices-heading" className="studio-billing-section">
                    <div className="studio-billing-section-heading">
                        <h2 id="invoices-heading">Recent invoices</h2>
                        <Link className="studio-billing-all-invoices" to="/billing/invoices">View all invoices <LucideIcon.ArrowRight size={15} /></Link>
                    </div>
                    <div className="studio-billing-invoices">
                        {invoicesLoading ? (
                            <div className="studio-billing-invoice-empty" role="status"><Spinner size={64} /><p>Loading invoices…</p></div>
                        ) : !invoices.length ? (
                            <div className="studio-billing-invoice-empty"><h3>No invoices yet</h3><p>Invoices will appear here after your first payment.</p></div>
                        ) : invoices.map((invoice: Invoice) => {
                            const invoiceSite = sites.find(site => site.tenant_id === invoice.tenant_id);
                            return (
                                <article key={invoice.id} className="studio-billing-invoice-row">
                                    <div className="studio-billing-invoice-copy">
                                        <div><strong>{invoice.invoice_number}</strong><InvoiceStatusBadge status={invoice.status} />{invoiceSite && sites.length > 1 && <span className="studio-billing-site-badge">{invoiceSite.site_name}</span>}</div>
                                        <p>{formatDate(invoice.paid_at || invoice.created_at)}{invoice.description && ` — ${invoice.description}`}</p>
                                    </div>
                                    <strong className="studio-billing-invoice-amount">{formatCurrency(invoice.amount_cents, invoice.currency)}</strong>
                                    {invoice.status === 'paid' && <button aria-label={`Download invoice ${invoice.invoice_number}`} className="studio-billing-download" type="button" onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}><LucideIcon.Download size={15} />PDF</button>}
                                </article>
                            );
                        })}
                    </div>
                </section>
            </>}

            <CreateNewsletterDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
        </div>
    );
}
